import { gql } from '@apollo/client';

/**
 * Tags and lists, changed by hand (card 19): on one contact from their page, or on a
 * selection of the contact list (rows ticked, or everyone the filter matches).
 *
 * Requests and real responses: CRM_CONTACT_QUERY_FRONTEND.md §17 (tags, card 15)
 * and §22 (lists and the selection, card 19). Every one is admin-only.
 */

/* ------------------------------------------------------------------ *
 * Choices
 * ------------------------------------------------------------------ */

/**
 * Every tag with its count across the CRM. The same root field and fields the
 * contact list's filter choices ask, so a visit that opened the list answers
 * this from the cache.
 */
export const CRM_TAG_CHOICES = gql`
  query CrmTagChoices {
    crmTags {
      id
      name
      color
      isSystem
      contactCount
    }
  }
`;

/** Lists people can be put on (STATIC): the 200 largest, or those whose name holds `search`. */
export const CRM_LIST_CHOICES = gql`
  query CrmListChoices($search: String) {
    crmSegments(search: $search, type: STATIC, take: 200) {
      total
      items {
        id
        name
        contactCount
      }
    }
  }
`;

/**
 * The exact size of a selection, for the confirmation. Never the list's
 * estimated total: the change is refused on any other number.
 */
export const CRM_SELECTION_COUNT = gql`
  query CrmSelectionCount($filter: CrmContactFilter) {
    crmContactCounts(filter: $filter) {
      total
    }
  }
`;

/** How many of a selection carry each tag: "1,014 of these already have it". */
export const CRM_SELECTION_TAGS = gql`
  query CrmSelectionTags($filter: CrmContactFilter) {
    crmContactFacet(field: TAG, filter: $filter, take: 200) {
      value
      label
      count
    }
  }
`;

/** How many of a selection are on each list. */
export const CRM_SELECTION_LISTS = gql`
  query CrmSelectionLists($filter: CrmContactFilter) {
    crmContactFacet(field: SEGMENT, filter: $filter, take: 200) {
      value
      label
      count
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Making a tag or a list
 * ------------------------------------------------------------------ */

export const CRM_CREATE_TAG = gql`
  mutation CrmCreateTag($name: String!) {
    crmCreateTag(name: $name) {
      id
      name
      color
      isSystem
      contactCount
    }
  }
`;

export const CRM_CREATE_LIST = gql`
  mutation CrmCreateList($name: String!) {
    crmCreateList(name: $name) {
      id
      name
      contactCount
    }
  }
`;

/* ------------------------------------------------------------------ *
 * One contact. The tag mutations return the contact, so its page
 * redraws its tags from the answer.
 * ------------------------------------------------------------------ */

const CONTACT_TAGS = `
  id
  tags {
    tagId
    name
    category
    color
    isSystem
    addedAt
    source
  }
`;

export const CRM_ADD_CONTACT_TAGS = gql`
  mutation CrmAddContactTags($contactId: Int!, $tagIds: [Int!]!) {
    crmAddContactTags(contactId: $contactId, tagIds: $tagIds) {
      ${CONTACT_TAGS}
    }
  }
`;

export const CRM_REMOVE_CONTACT_TAGS = gql`
  mutation CrmRemoveContactTags($contactId: Int!, $tagIds: [Int!]!) {
    crmRemoveContactTags(contactId: $contactId, tagIds: $tagIds) {
      ${CONTACT_TAGS}
    }
  }
`;

const LIST_CHANGE = `
  matched
  changed
  list {
    id
    name
    contactCount
  }
`;

export const CRM_ADD_TO_LIST = gql`
  mutation CrmAddToList($listId: Int!, $contactIds: [Int!]!) {
    crmAddToList(listId: $listId, contactIds: $contactIds) {
      ${LIST_CHANGE}
    }
  }
`;

export const CRM_REMOVE_FROM_LIST = gql`
  mutation CrmRemoveFromList($listId: Int!, $contactIds: [Int!]!) {
    crmRemoveFromList(listId: $listId, contactIds: $contactIds) {
      ${LIST_CHANGE}
    }
  }
`;

/* ------------------------------------------------------------------ *
 * A selection: the ticked rows as `{ ids }`, or the list's own filter.
 * `expectedCount` is the number the admin confirmed; on any other the
 * API changes nothing and answers CRM_SELECTION_CHANGED.
 * ------------------------------------------------------------------ */

const TAG_CHANGE = `
  matched
  tags {
    changed
    tag {
      id
      name
      color
      contactCount
    }
  }
`;

export const CRM_BULK_ADD_TAGS = gql`
  mutation CrmBulkAddTags($filter: CrmContactFilter!, $tagIds: [Int!]!, $expectedCount: Int!) {
    crmBulkAddTags(filter: $filter, tagIds: $tagIds, expectedCount: $expectedCount) {
      ${TAG_CHANGE}
    }
  }
`;

export const CRM_BULK_REMOVE_TAGS = gql`
  mutation CrmBulkRemoveTags($filter: CrmContactFilter!, $tagIds: [Int!]!, $expectedCount: Int!) {
    crmBulkRemoveTags(filter: $filter, tagIds: $tagIds, expectedCount: $expectedCount) {
      ${TAG_CHANGE}
    }
  }
`;

export const CRM_BULK_ADD_TO_LIST = gql`
  mutation CrmBulkAddToList($listId: Int!, $filter: CrmContactFilter!, $expectedCount: Int!) {
    crmBulkAddToList(listId: $listId, filter: $filter, expectedCount: $expectedCount) {
      ${LIST_CHANGE}
    }
  }
`;

export const CRM_BULK_REMOVE_FROM_LIST = gql`
  mutation CrmBulkRemoveFromList($listId: Int!, $filter: CrmContactFilter!, $expectedCount: Int!) {
    crmBulkRemoveFromList(listId: $listId, filter: $filter, expectedCount: $expectedCount) {
      ${LIST_CHANGE}
    }
  }
`;
